/**
 * @file `Courses` — namespacer for `lab.courses.*` catalog reads.
 * @module @labcraft/lab/src/client/courses
 *
 * Catalog reads — `list()` and `get(courseId)` hit lab's HTTP surface
 * (`GET /api/courses`, `GET /api/courses/:courseId`). The chapter
 * page's MDX module load goes through `chapter.module.url` from the
 * combined chapter endpoint, not through this namespacer.
 *
 * The MDX-thunk-shaped `defaultChapter` / `resolveChapter` /
 * `getNeighbors` methods are gone; consumers derive what they need
 * from the typed responses directly. The SPA's `pages/chapter.tsx`
 * computes its prev/next from `response.content.neighbors`, which
 * already carries the per-course flat-walk.
 */

import { CoreLifecycle } from '@labcraft/core'
import { defineRoute } from '@labcraft/core'
import type { HttpClientTransport } from '@labcraft/core/frontend'
import { z } from 'zod'
import {
  CourseCatalogEntrySchema,
  CourseHeaderSchema,
  GetCourseRoute,
  GetCoursesRoute,
  GetProgressRoute,
  PartListingSchema,
  UpcomingPartSchema,
  type CourseCatalogEntry,
  type CourseHeader,
  type PartListing,
  type UpcomingPart,
} from '../shared/routes.ts'

/**
 * Combined response from `GET /api/courses/:courseId` — course
 * header + parts listing. Mirrors the lab service's `describe()`
 * return value.
 *
 * @property course - course-scoped header data
 * @property parts - every built Part of the course with chapter listings
 * @property upcomingParts - the course's planned-but-unbuilt Parts
 *   (own-course `designed` graph nodes), rendered as `Upcoming` rows
 */
export type CourseSummary = {
  course: CourseHeader
  parts: PartListing[]
  upcomingParts: UpcomingPart[]
}

const CourseSummarySchema = z.object({
  course: CourseHeaderSchema,
  parts: z.array(PartListingSchema),
  upcomingParts: z.array(UpcomingPartSchema),
})

const GetCourseSummaryRoute = defineRoute({
  ...GetCourseRoute,
  response: CourseSummarySchema,
})

const GetCoursesArraySchema = z.array(CourseCatalogEntrySchema)
const GetCoursesArrayRoute = defineRoute({
  ...GetCoursesRoute,
  response: GetCoursesArraySchema,
})

/**
 * Required injected deps for `Courses` (SPA client side).
 *
 * @property http - the lab-origin HTTP transport. Reads catalog +
 *   per-course endpoints via typed `request(...)` calls.
 */
type HttpTransport = Pick<HttpClientTransport, 'request' | 'stream'>

/**
 * Catalog namespacer. Carries no in-memory state — every method
 * hits the lab API. TanStack query hooks in the React layer wrap
 * these calls for de-dup + cache.
 */
export class Courses extends CoreLifecycle<{ id?: string }> {
  private readonly http: HttpTransport

  constructor(http: HttpTransport) {
    super({ id: 'courses' })
    this.http = http
  }

  /** `GET /api/courses` — catalog list. */
  async list(): Promise<CourseCatalogEntry[]> {
    return this.http.request(GetCoursesArrayRoute)
  }

  /**
   * `GET /api/courses/:courseId` — course header + parts listing.
   * Returns `null` for an unknown course (404 → null instead of
   * throwing; the catalog UX treats unknown courses as "navigate
   * back to /courses").
   */
  async get(courseId: string): Promise<CourseSummary | null> {
    try {
      return await this.http.request(GetCourseSummaryRoute, {
        params: { courseId },
      })
    } catch (err) {
      if (
        err instanceof Error &&
        'metadata' in err &&
        (err as { metadata?: { status?: number } }).metadata?.status === 404
      ) {
        return null
      }
      throw err
    }
  }

  /**
   * `GET /api/progress/:courseId` — the learner's per-Part assessment
   * pass state (umbrella #1446 Part B). Auth-scoped; the course landing
   * joins the returned `passedPartIds` into the Parts path so a passed
   * Part renders **Done** regardless of the `currentChapter` pointer
   * (the fix for the completion loop). Kept a separate call from
   * {@link get} so per-learner state never rides the public course
   * summary.
   *
   * Throws on any transport error rather than swallowing it into `[]` —
   * masking a failure as "nothing passed" would show a passed Part as
   * incomplete and re-open the completion loop. The UI caller (the
   * landing's `usePassedPartIds` query) treats the throw as a retryable
   * error and surfaces it, instead of silently downgrading progress.
   */
  async passedPartIds(courseId: string): Promise<string[]> {
    const { passedPartIds } = await this.http.request(GetProgressRoute, {
      params: { courseId },
    })
    return passedPartIds
  }
}
